/**
 * The consent page's four query values and how they survive the portal's own sign-in (plan-034 WAPP-APPSIGNIN; design
 * §45.4 steps 1–2; ADR 0089).
 *
 * The app opens `/app-sign-in?code_challenge=…&code_challenge_method=S256&state=…&device_platform=ios|android`. The
 * page accepts exactly that shape — a challenge of 43 base64url characters, the method `S256` and nothing else, a
 * state of 22 base64url characters, the platform `ios` or `android` — and refuses anything else without naming a value.
 *
 * ── WHY THE VALUES RIDE IN THE TAB'S sessionStorage (the check the plan handed this unit) ─────────────────────────────
 *
 * The portal's sign-in has no return path of its own: `/log-in` lands on `/dashboard` (or `/first-run`, or the chosen
 * plan) after a password, an emailed code, the second step or a chooser; a Google or Microsoft sign-in comes back to
 * `/log-in?provider=signed_in` (the entry card's own `providerReturnPathOf`, whatever `safeReturnPath` would keep); an
 * organization's single sign-on comes back to `/`. So the page's query would be lost on every path. Before sending a
 * signed-out visitor to `/log-in`, the page keeps the four values here with the time and the fact that this sign-in
 * is being made for the app (D-25). Every one of those paths then enters the shell, and the shell's guard
 * (`router.tsx`) asks {@link appSignInReturnOf} once: a fresh hold sends the person back to `/app-sign-in` with the
 * same four values. On arrival the page marks the hold `arrived`, so the portal is never bounced back to this page
 * again, while a reload of the page still knows the sign-in was made for the app.
 *
 * The hold carries no secret: the challenge is public by design, the state is the app's to check, and the code is never
 * written here. It lives at most {@link APP_SIGN_IN_HOLD_MS} — the app's own pending sign-in lives no longer (D-26).
 */
import type { DevicePlatform } from "@wetalk/domain/identity/client-kind";

/** The four values the app put in the address. */
export interface AppSignInRequest {
  readonly codeChallenge: string;
  readonly state: string;
  readonly devicePlatform: DevicePlatform;
}

/** `/app-sign-in`'s search as the router validates it: the raw text of each value, or nothing. */
export interface AppSignInSearch {
  readonly code_challenge?: string;
  readonly code_challenge_method?: string;
  readonly state?: string;
  readonly device_platform?: string;
}

/** The app's pending sign-in lives 10 minutes (D-26); a hold older than that is ignored. */
export const APP_SIGN_IN_HOLD_MS = 10 * 60 * 1000;

/** The tab's storage key. */
export const APP_SIGN_IN_HOLD_KEY = "wetalk.app-sign-in";

const CHALLENGE_SHAPE = /^[A-Za-z0-9_-]{43}$/;
const STATE_SHAPE = /^[A-Za-z0-9_-]{22}$/;

function textOf(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** The router's `validateSearch`: text values only, everything else dropped (judged by {@link appSignInRequestOf}). */
export function appSignInSearchOf(raw: Readonly<Record<string, unknown>>): AppSignInSearch {
  const codeChallenge = textOf(raw["code_challenge"]);
  const method = textOf(raw["code_challenge_method"]);
  const state = textOf(raw["state"]);
  const platform = textOf(raw["device_platform"]);
  return {
    ...(codeChallenge === undefined ? {} : { code_challenge: codeChallenge }),
    ...(method === undefined ? {} : { code_challenge_method: method }),
    ...(state === undefined ? {} : { state }),
    ...(platform === undefined ? {} : { device_platform: platform }),
  };
}

/** The four values when every one has its shape, else `null` (the page then says only that the link cannot be used). */
export function appSignInRequestOf(search: AppSignInSearch): AppSignInRequest | null {
  const { code_challenge: codeChallenge, code_challenge_method: method, state, device_platform: platform } = search;
  if (codeChallenge === undefined || !CHALLENGE_SHAPE.test(codeChallenge)) return null;
  if (method !== "S256") return null;
  if (state === undefined || !STATE_SHAPE.test(state)) return null;
  if (platform !== "ios" && platform !== "android") return null;
  return { codeChallenge, state, devicePlatform: platform };
}

/** The search that names `request` again — what the shell's guard sends the person back with. */
export function appSignInSearchFor(request: AppSignInRequest): Required<AppSignInSearch> {
  return {
    code_challenge: request.codeChallenge,
    code_challenge_method: "S256",
    state: request.state,
    device_platform: request.devicePlatform,
  };
}

// ── the hold ──────────────────────────────────────────────────────────────────────────────────────────────

/** `signing_in`: on the way through `/log-in`; `arrived`: back on the page, the guard no longer redirects. */
export type AppSignInHoldPhase = "signing_in" | "arrived";

export interface AppSignInHold {
  readonly request: AppSignInRequest;
  readonly phase: AppSignInHoldPhase;
  readonly heldAtMs: number;
}

/** The slice of `Storage` the hold uses — `window.sessionStorage` in the browser, a map in a suite. */
export type HoldStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** The tab's storage, or `null` where the browser refuses it (a locked-down profile): the hold is then not kept. */
export function tabStorage(): HoldStorage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** Whether the tab keeps what is written (a private window may refuse every write). */
export function tabStorageKeeps(storage: HoldStorage | null): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(`${APP_SIGN_IN_HOLD_KEY}.probe`, "1");
    storage.removeItem(`${APP_SIGN_IN_HOLD_KEY}.probe`);
    return true;
  } catch {
    return false;
  }
}

/** The hold, when there is a fresh, well-formed one; anything else is removed and answered `null`. */
export function appSignInHoldOf(storage: HoldStorage | null, nowMs: number): AppSignInHold | null {
  if (storage === null) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(APP_SIGN_IN_HOLD_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const hold = holdOfText(raw);
  if (hold === null || nowMs - hold.heldAtMs > APP_SIGN_IN_HOLD_MS || hold.heldAtMs > nowMs) {
    clearAppSignInHold(storage);
    return null;
  }
  return hold;
}

function holdOfText(raw: string): AppSignInHold | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const record = parsed as Record<string, unknown>;
  const request = appSignInRequestOf(appSignInSearchOf(typeof record["request"] === "object" && record["request"] !== null ? (record["request"] as Record<string, unknown>) : {}));
  const phase = record["phase"];
  const heldAtMs = record["held_at_ms"];
  if (request === null || (phase !== "signing_in" && phase !== "arrived") || typeof heldAtMs !== "number" || !Number.isFinite(heldAtMs)) return null;
  return { request, phase, heldAtMs };
}

/** Keeps `request` for the tab; `false` when the browser refuses storage (the page then cannot come back by itself). */
export function holdAppSignIn(storage: HoldStorage | null, request: AppSignInRequest, phase: AppSignInHoldPhase, nowMs: number): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(APP_SIGN_IN_HOLD_KEY, JSON.stringify({ request: appSignInSearchFor(request), phase, held_at_ms: nowMs }));
    return true;
  } catch {
    return false;
  }
}

export function clearAppSignInHold(storage: HoldStorage | null): void {
  try {
    storage?.removeItem(APP_SIGN_IN_HOLD_KEY);
  } catch {
    // Nothing kept, nothing to remove.
  }
}

/** Whether the browser sign-in in use was made on the way, for this same request (D-25's fact). */
export function madeForAppSignIn(hold: AppSignInHold | null, request: AppSignInRequest): boolean {
  return hold !== null && hold.request.codeChallenge === request.codeChallenge && hold.request.state === request.state && hold.request.devicePlatform === request.devicePlatform;
}

/**
 * The shell's guard asks this once a person is signed in: the search to send them back to `/app-sign-in` with while a
 * hold is on its way through the sign-in, else `null`.
 */
export function appSignInReturnOf(storage: HoldStorage | null, nowMs: number): Required<AppSignInSearch> | null {
  const hold = appSignInHoldOf(storage, nowMs);
  return hold === null || hold.phase !== "signing_in" ? null : appSignInSearchFor(hold.request);
}

// ── the return ────────────────────────────────────────────────────────────────────────────────────────────

/**
 * `return_uri?code=…&state=…`. The return address is the api's, exactly as answered (resolved against the page only
 * when it is relative — the sandbox's); the page adds the two values and nothing else.
 */
export function appSignInTargetOf(returnUri: string, code: string, state: string, pageHref: string): string {
  const target = new URL(returnUri, pageHref);
  target.searchParams.set("code", code);
  target.searchParams.set("state", state);
  return target.toString();
}

/**
 * Leaving the portal for the app. One object so a suite can watch the navigation (a browser cannot follow
 * `io.wetalk.app:` in a test document), never so a caller can choose where to go.
 */
export const APP_SIGN_IN_EXIT = {
  leave(target: string): void {
    window.location.assign(target);
  },
};
